// Synthesized notification + ring tones for Fowlguard. Everything is
// generated at call time with the Web Audio API — no recorded mp3/wav
// assets to ship, licence, or cache. Every sound is a small chord of
// oscillators piped through a shared AudioContext with a short ADSR
// envelope so the tones sound musical instead of beep-y.
//
// Shared across every SPA via @fg/ui-theme so mail-web, connect-web,
// and drive-web all ring the same way.

let sharedCtx: AudioContext | null = null;
let gestureUnlockArmed = false;

/**
 * Browsers require a user gesture before an AudioContext can produce
 * sound. If the FIRST consumer of `ctx()` is a SignalR event (no gesture
 * on the call stack), the context is created in `suspended` state and
 * `resume()` returns a rejected promise — the chat tone never plays.
 *
 * Solution: arm a one-time `pointerdown` / `keydown` / `touchstart`
 * listener at module load. The first time the user interacts with the
 * page, we create + resume the context. Subsequent `ctx()` calls find a
 * running context and play instantly.
 */
function armGestureUnlock(): void {
    if (gestureUnlockArmed || typeof window === "undefined") return;
    gestureUnlockArmed = true;
    const handler = () => {
        try {
            const ac = rawCtx();
            if (ac.state === "suspended") void ac.resume();
        } catch { /* AudioContext unsupported — silent */ }
        window.removeEventListener("pointerdown", handler, true);
        window.removeEventListener("keydown", handler, true);
        window.removeEventListener("touchstart", handler, true);
    };
    window.addEventListener("pointerdown", handler, { capture: true, once: false });
    window.addEventListener("keydown", handler, { capture: true, once: false });
    window.addEventListener("touchstart", handler, { capture: true, once: false });
}
if (typeof window !== "undefined") armGestureUnlock();

function rawCtx(): AudioContext {
    if (!sharedCtx) {
        const Ctor = (window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext);
        sharedCtx = new Ctor();
    }
    return sharedCtx;
}

function ctx(): AudioContext {
    const ac = rawCtx();
    // Best-effort resume — works if a user gesture has happened in the
    // tab already. The arm listener above guarantees that any tab the
    // user has clicked / typed in is unlocked the moment they do so.
    if (ac.state === "suspended") void ac.resume();
    return ac;
}

type Envelope = { attack: number; decay: number; sustain: number; release: number };
const DEFAULT_ENV: Envelope = { attack: 0.01, decay: 0.05, sustain: 0.5, release: 0.15 };

function playNote(freq: number, startAt: number, duration: number, gain = 0.2, env: Envelope = DEFAULT_ENV, waveform: OscillatorType = "sine") {
    const ac = ctx();
    const osc = ac.createOscillator();
    const vca = ac.createGain();
    osc.type = waveform;
    osc.frequency.setValueAtTime(freq, startAt);
    osc.connect(vca);
    vca.connect(ac.destination);

    // Piecewise ADSR — `setValueAtTime` + `linearRamp` beats
    // `exponentialRamp` here because the start/end values can hit zero
    // without DOMException.
    const peak = gain;
    const sustainLevel = peak * env.sustain;
    vca.gain.setValueAtTime(0, startAt);
    vca.gain.linearRampToValueAtTime(peak, startAt + env.attack);
    vca.gain.linearRampToValueAtTime(sustainLevel, startAt + env.attack + env.decay);
    vca.gain.setValueAtTime(sustainLevel, startAt + duration - env.release);
    vca.gain.linearRampToValueAtTime(0, startAt + duration);

    osc.start(startAt);
    osc.stop(startAt + duration + 0.02);
}

/**
 * Short two-note ping for arriving chat messages. Plays once. Safe to
 * spam — each call schedules its own oscillators so concurrent arrivals
 * layer rather than glitch.
 *
 *   C6 (1046.5 Hz) → E6 (1318.5 Hz), 60 ms each, ~120 ms total.
 */
export function playMessageTone(): void {
    const ac = ctx();
    const t0 = ac.currentTime + 0.01;
    playNote(1046.5, t0,          0.12, 0.18, DEFAULT_ENV, "triangle");
    playNote(1318.5, t0 + 0.09,   0.14, 0.2,  DEFAULT_ENV, "triangle");
}

/**
 * Repeating musical phrase for incoming calls and meeting invites. The
 * caller keeps the returned handle and invokes `stop()` when the user
 * answers / declines / the timeout lapses. The pattern loops every
 * 2.4 seconds so there's a comfortable silence between rings.
 *
 * Pattern (original — not lifted from any existing ringtone):
 *   E5 A5 C6 A5 (beat 1)
 *   — — — — (rest)
 *   E5 A5 C6 D6 (beat 2)
 *   — — — — (rest)
 */
export interface Ringtone {
    stop: () => void;
}

export function playRingtone(volume = 0.22): Ringtone {
    const ac = ctx();
    let cancelled = false;
    const queued: number[] = [];

    const pattern: Array<{ freq: number; offset: number; dur: number }> = [
        { freq: 659.3, offset: 0.00, dur: 0.18 }, // E5
        { freq: 880.0, offset: 0.20, dur: 0.18 }, // A5
        { freq: 1046.5, offset: 0.40, dur: 0.18 }, // C6
        { freq: 880.0, offset: 0.60, dur: 0.22 }, // A5
        { freq: 659.3, offset: 1.00, dur: 0.18 }, // E5
        { freq: 880.0, offset: 1.20, dur: 0.18 }, // A5
        { freq: 1046.5, offset: 1.40, dur: 0.18 }, // C6
        { freq: 1174.7, offset: 1.60, dur: 0.32 }  // D6 (hold)
    ];
    const loopEvery = 2.4;

    const scheduleLoop = () => {
        if (cancelled) return;
        const start = ac.currentTime + 0.02;
        for (const n of pattern) {
            playNote(n.freq, start + n.offset, n.dur, volume, DEFAULT_ENV, "triangle");
        }
        // Re-arm at loopEvery seconds. We use a window.setTimeout so stop()
        // can cancel it immediately — you can't reliably cancel a
        // setTimeout scheduled on the AudioContext clock.
        const handle = window.setTimeout(scheduleLoop, loopEvery * 1000);
        queued.push(handle);
    };
    scheduleLoop();

    return {
        stop: () => {
            cancelled = true;
            for (const h of queued) window.clearTimeout(h);
            queued.length = 0;
        }
    };
}

/**
 * Same shape as playRingtone but for meeting invites — longer and
 * calmer (meetings aren't as urgent as an incoming call). If both an
 * invite AND a call land together, callers can prefer the ringtone
 * and mute this one.
 */
export function playMeetingInviteTone(): Ringtone {
    return playRingtone(0.16);
}
